import {
    AccountNode,
    ContactNode,
    EcnNode,
    ProductNode,
    QuoteNode,
    SaleNode,
    SeederNode,
    SupplyOrderNode,
    WarrantyClaimNode,
    QuotedProductFormInput,
    GetProductsStocksQuery,
    QuotedProductFragment,
    SupplyOrderDetailFragment,
    GetProductsEffectiveDatesQuery,
    WarrantyProductFragment,
} from 'generated/graphql'
import {extendedQuantityOf} from 'utils/quotes/calculator'

export interface Hash<T> {
    [key: string]: T
}

export const getObjectKeys = <T extends object>(value: T) => Object.keys(value) as (keyof T)[]
export const getObjectEntries = <T extends object>(value: T) => Object.entries(value) as [keyof T, T[keyof T]][]

export const getHash = <T extends object>(items: T[], key: Only<T, string>) => {
    return items.reduce<Hash<T>>((hash: Hash<T>, item: T) => {
        const hashKey = item[key]
        if (typeof hashKey !== 'string')
            throw new Error('This error should never happend "Only<T, string>" should allow keys that return strings')
        hash[hashKey as string] = item
        return hash
    }, {})
}

export const encodeToB64 = (object: any) => {
    return Buffer.from(JSON.stringify(object)).toString('base64')
}

export const decodeFromB64 = (value: string) => {
    return JSON.parse(Buffer.from(value, 'base64').toString())
}

export const openData = ({data, filename, contentType}: {data: string; filename: string; contentType: string}) => {
    const link = document.createElement('a')
    link.href = `data:${contentType};filename=${filename};base64,${data}`
    link.setAttribute('download', filename)
    link.click()
}

export const notEmpty = <T>(value: T | undefined | null): value is T => value !== undefined && value !== null

export const isUrlPdf = (url: string) => {
    return url.split('?').shift()?.endsWith('.pdf')
}

export const notUndefined = <T>(value: T | undefined): value is T => value !== undefined

export function toGlobalId(typename: Typenames, uuid: string) {
    if (typeof window === undefined) throw new Error('Only client function')
    if (!checkIfValidUUID(uuid)) {
        return uuid
    }
    return btoa(`${typename}:${uuid}`)
}

type StockLine = {
    externalId: string
    quantity: number
    bundleName?: string | null
    bundleQuantity?: number | null
}

/** Units this line needs. A bundle line is component quantity × how many bundles were quoted. */
export const demandedUnits = (product: StockLine) => extendedQuantityOf(product)

const toStockLine = (
    product: QuotedProductFormInput | QuotedProductFragment | WarrantyProductFragment,
): StockLine => ({
    externalId: product.externalId,
    quantity: product.quantity,
    bundleName: 'bundleName' in product ? product.bundleName : null,
    bundleQuantity: 'bundleQuantity' in product ? product.bundleQuantity : null,
})

const stockRowFor = (externalId: string | null | undefined, stockData?: GetProductsStocksQuery) => {
    if (!externalId) return {state: 'missing' as const}
    const stocks = stockData?.productsStocks?.stocks
    if (!stocks) return {state: 'pending' as const}
    const stock = stocks.find(item => item.externalId === externalId)
    if (!stock || !stock.found) return {state: 'missing' as const}
    return {state: 'found' as const, availableQuantity: stock.availableQuantity}
}

export const productHasStock = (
    product: QuotedProductFormInput | QuotedProductFragment | WarrantyProductFragment,
    stockData?: GetProductsStocksQuery,
    lines?: StockLine[],
) => {
    const stock = stockRowFor(product.externalId, stockData)
    if (stock.state === 'pending') return true
    if (stock.state === 'missing') return false
    const stockLines: StockLine[] = lines ?? [toStockLine(product)]
    const demanded = stockLines.reduce((total, line) => {
        if (line.externalId !== product.externalId) return total
        return total + demandedUnits(line)
    }, 0)
    return stock.availableQuantity >= demanded
}

export const productHasStockForOrder = (
    product: {externalId: string},
    orderedQuantity: number,
    stockData?: GetProductsStocksQuery,
) => {
    const stock = stockRowFor(product.externalId, stockData)
    if (stock.state === 'pending') return true
    if (stock.state === 'missing') return false
    return stock.availableQuantity >= orderedQuantity
}

/** A line with nothing ordered is fine. Otherwise stock must cover every line of that part. */
export const orderedLinesHaveStock = (
    lines: Array<{externalId: string; quantity: number}>,
    stockData?: GetProductsStocksQuery,
) => {
    const demanded = new Map<string, number>()
    for (const line of lines) {
        demanded.set(line.externalId, (demanded.get(line.externalId) ?? 0) + line.quantity)
    }
    return (externalId: string, lineQuantity: number) => {
        if (lineQuantity <= 0) return true
        return productHasStockForOrder(
            {externalId},
            demanded.get(externalId) ?? lineQuantity,
            stockData,
        )
    }
}

function checkIfValidUUID(str: string) {
    // Regular expression to check if string is a valid UUID
    const regexExp = /^[0-9a-fA-F]{8}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{12}$/gi

    return regexExp.test(str)
}

type Typenames = NonNullable<
    | ProductNode['__typename']
    | AccountNode['__typename']
    | QuoteNode['__typename']
    | SupplyOrderNode['__typename']
    | SaleNode['__typename']
    | ContactNode['__typename']
    | WarrantyClaimNode['__typename']
    | SeederNode['__typename']
    | EcnNode['__typename']
>

export const unwrapEdges = <
    T extends undefined | {edges: Array<{node?: unknown}>},
    TNode = NonNullable<NonNullable<T>['edges'][number]['node']>,
>(
    arr?: T,
): TNode[] => {
    if (!arr) return []
    const result = arr.edges.map(edge => edge.node).filter(notEmpty)
    return result as TNode[]
}

export function getShipmentQuantityForQuoteProduct(
    supplyOrder: SupplyOrderDetailFragment | null | undefined,
    quoteProductId: string,
) {
    return supplyOrder?.shipments
        .flatMap(shipment => shipment.productsShipped)
        .filter(product => product.orderedProduct.quotedProduct?.id === quoteProductId)
        .reduce((acc, curr) => {
            return acc + curr.quantity
        }, 0)
}

export const productIsEffective = (
    product: {externalId: string},
    effectiveDatesData?: GetProductsEffectiveDatesQuery,
) => {
    if (!effectiveDatesData?.products?.edges) return true
    const productData = effectiveDatesData.products.edges.find(edge => edge.node?.externalId === product.externalId)
    return productData?.node?.isEffective ?? true
}
